import { useEffect, useState, useRef } from "react";
import apiClient from "@/lib/api";
import SearchableSelect from "@/components/ui/searchable-select";

function Select({ label, testid, value, onChange, options, disabled, loading, placeholder }) {
  const showEmpty = !loading && !disabled && options.length === 0;
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.15em] text-primary">{label}</label>
      <div className="mt-1.5">
        <SearchableSelect
          testid={testid}
          value={value}
          onChange={onChange}
          options={options}
          disabled={disabled || showEmpty}
          loading={loading}
          placeholder={showEmpty ? "Tidak ada data" : placeholder}
          searchPlaceholder="Cari..."
          emptyText="Tidak ditemukan."
        />
      </div>
    </div>
  );
}

export default function LocationSelector({ onChange, initialValue }) {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);

  const [sel, setSel] = useState({ province: "", city: "", district: "", subdistrict: "" });
  const [loading, setLoading] = useState({ province: false, city: false, district: false, subdistrict: false });
  const preloaded = useRef(false);

  const setLoad = (key, val) => setLoading((l) => ({ ...l, [key]: val }));
  const emit = (next) => onChange && onChange(next);

  // Load provinces on mount (+ optional edit preload chain)
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoad("province", true);
      let provs = [];
      try {
        const { data } = await apiClient.get("/locations/provinces");
        provs = data;
      } catch {
        provs = [];
      }
      if (cancelled) return;
      setProvinces(provs);
      setLoad("province", false);

      if (initialValue?.province && !preloaded.current) {
        preloaded.current = true;
        try {
          setLoad("city", true);
          const c = (await apiClient.get(`/locations/cities?province_id=${initialValue.province}`)).data;
          if (cancelled) return;
          setCities(c); setLoad("city", false);

          setLoad("district", true);
          const d = (await apiClient.get(`/locations/districts?city_id=${initialValue.city}`)).data;
          if (cancelled) return;
          setDistricts(d); setLoad("district", false);

          setLoad("subdistrict", true);
          const s = (await apiClient.get(`/locations/subdistricts?district_id=${initialValue.district}`)).data;
          if (cancelled) return;
          setSubdistricts(s); setLoad("subdistrict", false);

          const next = {
            province: initialValue.province,
            city: initialValue.city,
            district: initialValue.district,
            subdistrict: initialValue.subdistrict,
            province_name: provs.find((o) => o.id === initialValue.province)?.name || "",
            city_name: c.find((o) => o.id === initialValue.city)?.name || "",
            district_name: d.find((o) => o.id === initialValue.district)?.name || "",
            subdistrict_name: s.find((o) => o.id === initialValue.subdistrict)?.name || "",
          };
          setSel(next);
          emit(next);
        } catch {
          setLoad("city", false); setLoad("district", false); setLoad("subdistrict", false);
        }
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProvince = async (id) => {
    const next = {
      province: id, city: "", district: "", subdistrict: "",
      province_name: provinces.find((o) => o.id === id)?.name || "",
      city_name: "", district_name: "", subdistrict_name: "",
    };
    setSel(next); setCities([]); setDistricts([]); setSubdistricts([]); emit(next);
    if (!id) return;
    setLoad("city", true);
    try { setCities((await apiClient.get(`/locations/cities?province_id=${id}`)).data); }
    catch { setCities([]); }
    finally { setLoad("city", false); }
  };

  const handleCity = async (id) => {
    const next = {
      ...sel, city: id, district: "", subdistrict: "",
      city_name: cities.find((o) => o.id === id)?.name || "",
      district_name: "", subdistrict_name: "",
    };
    setSel(next); setDistricts([]); setSubdistricts([]); emit(next);
    if (!id) return;
    setLoad("district", true);
    try { setDistricts((await apiClient.get(`/locations/districts?city_id=${id}`)).data); }
    catch { setDistricts([]); }
    finally { setLoad("district", false); }
  };

  const handleDistrict = async (id) => {
    const next = {
      ...sel, district: id, subdistrict: "",
      district_name: districts.find((o) => o.id === id)?.name || "",
      subdistrict_name: "",
    };
    setSel(next); setSubdistricts([]); emit(next);
    if (!id) return;
    setLoad("subdistrict", true);
    try { setSubdistricts((await apiClient.get(`/locations/subdistricts?district_id=${id}`)).data); }
    catch { setSubdistricts([]); }
    finally { setLoad("subdistrict", false); }
  };

  const handleSubdistrict = (id) => {
    const next = {
      ...sel, subdistrict: id,
      subdistrict_name: subdistricts.find((o) => o.id === id)?.name || "",
    };
    setSel(next); emit(next);
  };

  return (
    <div className="grid gap-4" data-testid="location-selector">
      <Select label="Provinsi" testid="location-province-select" value={sel.province}
        onChange={handleProvince} options={provinces} loading={loading.province} placeholder="Pilih provinsi" />
      <Select label="Kota / Kabupaten" testid="location-city-select" value={sel.city}
        onChange={handleCity} options={cities} loading={loading.city} disabled={!sel.province}
        placeholder={sel.province ? "Pilih kota/kabupaten" : "Pilih provinsi dulu"} />
      <Select label="Kecamatan" testid="location-district-select" value={sel.district}
        onChange={handleDistrict} options={districts} loading={loading.district} disabled={!sel.city}
        placeholder={sel.city ? "Pilih kecamatan" : "Pilih kota dulu"} />
      <Select label="Kelurahan / Desa" testid="location-subdistrict-select" value={sel.subdistrict}
        onChange={handleSubdistrict} options={subdistricts} loading={loading.subdistrict} disabled={!sel.district}
        placeholder={sel.district ? "Pilih kelurahan/desa" : "Pilih kecamatan dulu"} />
    </div>
  );
}
